import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
// import { selectAllEntities } from '../store/entitiesSlice';
import { selectCommodities } from '../store/commoditiesSlice';

const OrderForm = ({ 
  onOrderSubmit,
  minQuantity = 1,
  maxQuantity = 10000,
}) => {
  const entities = useSelector(selectCommodities);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [orderType, setOrderType] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');

  const validateOrder = useCallback(() => {
    const newErrors = {};
    
    // Validate entity selection
    if (!selectedEntityId) {
      newErrors.entity = 'Please select an entity';
    }
    
    // Validate quantity
    if (!quantity || isNaN(quantity) || parseFloat(quantity) <= 0) {
      newErrors.quantity = 'Quantity must be a positive number';
    } else {
      const qty = parseFloat(quantity);
      if (qty < minQuantity) {
        newErrors.quantity = `Quantity must be at least ${minQuantity}`;
      } else if (qty > maxQuantity) {
        newErrors.quantity = `Quantity must be no more than ${maxQuantity}`;
      }
    }
    
    return newErrors;
  }, [selectedEntityId, quantity, minQuantity, maxQuantity]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const errors = validateOrder();
    setErrors(errors);

    if (Object.keys(errors).length === 0 && selectedEntity && onOrderSubmit) {
      const order = {
        //id: Date.now().toString(),
        entityId: selectedEntityId,
        entityName: selectedEntity.name,
        type: orderType,
        quantity: parseFloat(quantity),
        price: orderType === 'buy' ? selectedEntity.ask : selectedEntity.bid,
        timestamp: new Date().toISOString(),
        status: 'pending',
        unit: selectedEntity.size,
      };

      onOrderSubmit(order);
      
      // Reset form
      setQuantity('');
      setErrors({});
    }
  }, [selectedEntityId, orderType, quantity, selectedEntity, validateOrder, onOrderSubmit]);

  const calculateOrderValue = () => {
    if (selectedEntity && quantity && !isNaN(quantity)) {
      const price = orderType === 'buy' ? selectedEntity.ask : selectedEntity.bid;
      return (price * parseFloat(quantity)).toFixed(2);
    }
    return '0.00';
  };

  const isFormValid = Object.keys(errors).length === 0 && 
    selectedEntityId && 
    quantity && 
    !isNaN(quantity);

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setSelectedSize(entity.size || '');
  };

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: 8,
      padding: 20,
      maxWidth: 500,
      background: '#fafcff',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
        Place Order
      </h3>

      <form onSubmit={handleSubmit}>
        {/* Entity Selection */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555' }}>
            Select Commodity:
          </label>
          <select
            value={selectedEntityId}
            onChange={(e) => {
              const entity = entities.find(ent => ent.id === e.target.value);
              setSelectedEntityId(e.target.value);
              setSelectedEntity(entity);
              setSelectedSize(entity ? entity.size : '');
            }}
            style={{
              width: '100%',
              padding: 8,
              border: errors.entity ? '1px solid #f44336' : '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14,
              background: 'white'
            }}
          >
            <option value="">Choose an entity...</option>
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {`${entity.name} (${entity.contractMonth} ${entity.contractYear})`}
              </option>
            ))}
          </select>
          {errors.entity && (
            <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
              {errors.entity}
            </div>
          )}
        </div>

        {/* Display selected entity size */}
        {selectedSize && (
          <div style={{ marginTop: 8 }}>
            <strong>Size:</strong> {selectedSize}
          </div>
        )}

        {/* Order Type Selection */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555', marginTop: 8 }}>
            Order Type:
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="orderType"
                value="buy"
                checked={orderType === 'buy'}
                onChange={(e) => setOrderType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <span style={{ 
                color: '#4caf50', 
                fontWeight: 'bold',
                padding: '4px 8px',
                borderRadius: 4,
                background: orderType === 'buy' ? '#e8f5e8' : 'transparent'
              }}>
                Buy
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="orderType"
                value="sell"
                checked={orderType === 'sell'}
                onChange={(e) => setOrderType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <span style={{ 
                color: '#f44336', 
                fontWeight: 'bold',
                padding: '4px 8px',
                borderRadius: 4,
                background: orderType === 'sell' ? '#ffebee' : 'transparent'
              }}>
                Sell
              </span>
            </label>
          </div>
        </div>

        {/* Quantity Input */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555' }}>
            Quantity:
          </label>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter quantity"
            min={minQuantity}
            max={maxQuantity}
            step="1"
            style={{
              width: '100%',
              padding: 8,
              border: errors.quantity ? '1px solid #f44336' : '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14
            }}
          />
          {errors.quantity && (
            <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
              {errors.quantity}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Min: {minQuantity} | Max: {maxQuantity}
          </div>
        </div>

        {/* Order Summary */}
        {selectedEntity && quantity && !isNaN(quantity) && (
          <div style={{
            border: '1px solid #e0e0e0',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16,
            background: '#f8f9fa'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#555' }}>Order Summary</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
              <div>
                <strong>Commodity:</strong> {selectedEntity.name}
              </div>
              <div>
                <strong>Contract:</strong> {`${selectedEntity.contractMonth} ${selectedEntity.contractYear }`}
              </div>
              <div>
                <strong>Type:</strong> 
                <span style={{ 
                  color: orderType === 'buy' ? '#4caf50' : '#f44336',
                  fontWeight: 'bold',
                  marginLeft: 4
                }}>
                  {orderType.toUpperCase()}
                </span>
              </div>
              <div>
                <strong>Quantity:</strong> {parseFloat(quantity).toLocaleString()}
              </div>
              <div>
                <strong>Price:</strong> ${orderType === 'buy' ? selectedEntity.ask : selectedEntity.bid}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Total Value:</strong> 
                <span style={{ 
                  fontWeight: 'bold',
                  color: orderType === 'buy' ? '#4caf50' : '#f44336',
                  marginLeft: 4
                }}>
                  ${calculateOrderValue()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isFormValid}
          style={{
            width: '100%',
            padding: 12,
            backgroundColor: isFormValid ? (orderType === 'buy' ? '#4caf50' : '#f44336') : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: isFormValid ? 'pointer' : 'not-allowed',
            fontSize: 16,
            fontWeight: 'bold'
          }}
        >
          {orderType === 'buy' ? 'Place Buy Order' : 'Place Sell Order'}
        </button>
      </form>

      {/* No Entities Message */}
      {entities.length === 0 && (
        <div style={{
          marginTop: 16,
          padding: 12,
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 4,
          color: '#856404'
        }}>
          <strong>No entities available.</strong> Please add entities in the Entity Price Manager first.
        </div>
      )}
    </div>
  );
};

export default OrderForm;